import { useState } from 'react';

import { logger } from '@/services/logger';

const readFromStorage = <T>(key: string, fallback: T): T => {
    try {
        const storedValue = localStorage.getItem(key);
        return storedValue ? (JSON.parse(storedValue) as T) : fallback;
    } catch (error) {
        logger.warn(`useLocalStorage: failed to read "${key}", falling back to initial value`, error);
        return fallback;
    }
};

const writeToStorage = <T>(key: string, value: T) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        logger.warn(`useLocalStorage: failed to persist "${key}"`, error);
    }
};

const removeFromStorage = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        logger.warn(`useLocalStorage: failed to remove "${key}"`, error);
    }
};

type Updater<T> = T | ((prev: T) => T);

const isUpdaterFn = <T>(u: Updater<T>): u is (prev: T) => T => typeof u === 'function';

const useLocalStorage = <T>(key: string, initialValue: T) => {

    const [value, setValue] = useState<T>(() => readFromStorage(key, initialValue));

    // LAX-10039: accept the functional-updater form so callers that need to
    // merge into the latest committed value (e.g. multiple useEffects racing
    // on mount) don't clobber each other via stale closures over `value`.
    const setStoredValue = (newValue: Updater<T>) => {
        setValue((prev) => {
            const next = isUpdaterFn(newValue) ? newValue(prev) : newValue;
            writeToStorage(key, next);
            return next;
        });
    };

    const removeStoredValue = () => {
        removeFromStorage(key);
        setValue(initialValue);
    };

    return [value, setStoredValue, removeStoredValue] as const;
};

export { useLocalStorage };
